function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _classPrivateFieldGet(receiver, privateMap) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "get"); return _classApplyDescriptorGet(receiver, descriptor); }

function _classApplyDescriptorGet(receiver, descriptor) { if (descriptor.get) { return descriptor.get.call(receiver); } return descriptor.value; }

function _classPrivateFieldSet(receiver, privateMap, value) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "set"); _classApplyDescriptorSet(receiver, descriptor, value); return value; }

function _classExtractFieldDescriptor(receiver, privateMap, action) { if (!privateMap.has(receiver)) { throw new TypeError("attempted to " + action + " private field on non-instance"); } return privateMap.get(receiver); }

function _classApplyDescriptorSet(receiver, descriptor, value) { if (descriptor.set) { descriptor.set.call(receiver, value); } else { if (!descriptor.writable) { throw new TypeError("attempted to set read only private field"); } descriptor.value = value; } }

import ContractImpl from 'web3-eth-contract';
import { toHex, toChecksumAddress, isAddress, isHexStrict, utf8ToHex, toBN } from 'web3-utils';
import { AddChainError, ChainNotConfiguredError, SwitchChainError, UserRejectedRequestError } from '../errors';
import { normalizeChainId } from '../utils';
import { Connector } from './base';

/**
 * Wallets that support chain switching through WalletConnect
 * - imToken (token.im)
 * - MetaMask (metamask.io)
 * - Rainbow (rainbow.me)
 */
const switchChainAllowedRegex = /(imtoken|metamask|rainbow)/i;

var _provider = /*#__PURE__*/new WeakMap();

var _isSupportedRequestMethod = /*#__PURE__*/new WeakMap();

export class WalletConnectConnector extends Connector {
  constructor(config) {
    super(config);

    _defineProperty(this, "id", 'WalletConnect');

    _defineProperty(this, "name", 'WalletConnect');

    _defineProperty(this, "ready", true);

    _provider.set(this, {
      writable: true,
      value: void 0
    });

    _isSupportedRequestMethod.set(this, {
      writable: true,
      value: false
    });

    _defineProperty(this, "contracts", {});

    _defineProperty(this, "onAccountsChanged", accounts => {
      if (accounts.length === 0) this.emit('disconnect');else {
        this.emit('change', {
          account: toChecksumAddress(accounts[0])
        });
        this.emit('accountsChanged', {
          account: toChecksumAddress(accounts[0])
        });
      }
    });

    _defineProperty(this, "onChainChanged", chainId => {
      const id = normalizeChainId(chainId);
      const unsupported = this.isChainUnsupported(id);
      this.emit('change', {
        chain: {
          id,
          unsupported
        }
      });
      this.emit('chainChanged', {
        chain: {
          id,
          unsupported
        }
      });
    });

    _defineProperty(this, "onDisconnect", () => {
      this.emit('disconnect');
    });
  }

  async connect(chainConfigs) {
    try {
      var _provider$connector, _provider$connector$p;

      const {
        chainId
      } = chainConfigs || {};
      const targetChainId = chainId;
      const provider = await this.getProvider({
        chainId: targetChainId,
        create: true
      });
      provider.on('accountsChanged', this.onAccountsChanged);
      provider.on('chainChanged', this.onChainChanged);
      provider.on('disconnect', this.onDisconnect); // Defer message to the next tick to ensure wallet connect data (provided by `.enable()`) is available

      setTimeout(() => this.emit('message', {
        type: 'connecting'
      }), 0);
      const accounts = await provider.enable();
      const account = toChecksumAddress(accounts[0]);
      const id = await this.getChainId();
      const unsupported = this.isChainUnsupported(id); // Not all WalletConnect options support programmatic chain switching
      // Only enable for wallet options that do

      const walletName = ((_provider$connector = provider.connector) === null || _provider$connector === void 0 ? void 0 : (_provider$connector$p = _provider$connector.peerMeta) === null || _provider$connector$p === void 0 ? void 0 : _provider$connector$p.name) ?? '';
      if (switchChainAllowedRegex.test(walletName)) _classPrivateFieldSet(this, _isSupportedRequestMethod, true);
      return {
        account,
        chain: {
          id,
          unsupported
        },
        provider: provider
      };
    } catch (error) {
      if (/user closed modal/i.test(error.message)) throw new UserRejectedRequestError(error);
      throw error;
    }
  }

  async disconnect() {
    const provider = await this.getProvider();
    await provider.disconnect();
    provider.removeListener('accountsChanged', this.onAccountsChanged);
    provider.removeListener('chainChanged', this.onChainChanged);
    provider.removeListener('disconnect', this.onDisconnect);
    typeof localStorage !== 'undefined' && localStorage.removeItem('walletconnect');
  }

  async getContract(config) {
    const {
      address,
      contractInterface
    } = config || {};
    const provider = await this.getProvider();
    ContractImpl.setProvider(provider);

    if (!this.contracts[address]) {
      //@ts-expect-error hack because web3 library typedef doesn't match its JS implementation
      const contract = new ContractImpl(contractInterface, address);
      this.contracts[address] = this.attachProxy(contract);
    }

    return this.contracts[address];
  }

  async getAccount() {
    const provider = await this.getProvider();
    const accounts = provider.accounts; // return checksum address

    return toChecksumAddress(accounts[0]);
  }

  async getChainId() {
    const provider = await this.getProvider();
    const chainId = normalizeChainId(provider.chainId);
    return chainId;
  }

  async getProvider({
    chainId,
    create
  } = {}) {
    // Force create new provider
    if (!_classPrivateFieldGet(this, _provider) || chainId || create) {
      var _this$options, _this$options2;

      const chain = this.chains.find(chain => chain.id === chainId);
      const rpc = !((_this$options = this.options) !== null && _this$options !== void 0 && _this$options.infuraId) ? this.chains.reduce((rpc, chain) => ({ ...rpc,
        [chain.id]: chain.rpcUrls.default
      }), {}) : {};
      const rpcOption = chain !== null && chain !== void 0 && chain.testnet ? {} : (_this$options2 = this.options) === null || _this$options2 === void 0 ? void 0 : _this$options2.rpc;
      const WalletConnectProvider = (await import('@walletconnect/web3-provider')).default;

      _classPrivateFieldSet(this, _provider, new WalletConnectProvider({ ...this.options,
        chainId,
        rpc: { ...rpc,
          ...rpcOption
        }
      }));
    }

    return _classPrivateFieldGet(this, _provider);
  }

  async isAuthorized() {
    try {
      const account = await this.getAccount();
      return !!account;
    } catch {
      return false;
    }
  }

  async signMessage(args) {
    try {
      const {
        address,
        message
      } = args;
      const provider = await this.getProvider();
      const signature = await provider.request({
        method: 'personal_sign',
        params: [!isAddress(message) && isHexStrict(message) ? message : utf8ToHex(message), address, '']
      });
      return signature;
    } catch (error) {
      if (isUserRejectedRequestError(error)) throw new UserRejectedRequestError(error);
      throw error;
    }
  }

  async switchChain(chainId) {
    if (!_classPrivateFieldGet(this, _isSupportedRequestMethod)) {
      return {
        id: chainId,
        name: `switchChain not supported`,
        network: `unknown`,
        rpcUrls: {
          default: ''
        }
      };
    }

    const provider = await this.getProvider();
    const id = toHex(chainId);

    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{
          chainId: id
        }]
      });
      return this.chains.find(x => x.id === chainId) ?? {
        id: chainId,
        name: `Chain ${id}`,
        network: `${id}`,
        rpcUrls: {
          default: ''
        }
      };
    } catch (error) {
      const message = typeof error === 'string' ? error : error === null || error === void 0 ? void 0 : error.message;
      if (/user rejected request/i.test(message)) throw new UserRejectedRequestError(error);

      if (/Unrecognized chain/i.test(message)) {
        await this.addWalletChain(chainId);
      }

      throw new SwitchChainError(error);
    }
  }

  async addWalletChain(chainId) {
    try {
      var _chain$blockExplorers;

      const id = toHex(chainId);
      const provider = await this.getProvider();
      const chain = this.chains.find(x => x.id === chainId);
      if (!chain) throw new ChainNotConfiguredError();
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: id,
          chainName: chain.name,
          nativeCurrency: chain.nativeCurrency,
          rpcUrls: [chain.rpcUrls.default],
          blockExplorerUrls: (_chain$blockExplorers = chain.blockExplorers) !== null && _chain$blockExplorers !== void 0 && _chain$blockExplorers.default ? [chain.blockExplorers.default.url] : undefined
        }]
      });
      return chain;
    } catch (error) {
      if (isUserRejectedRequestError(error)) throw new UserRejectedRequestError(error);
      throw new AddChainError();
    }
  }

  async watchAsset({
    address,
    decimals = 18,
    image,
    symbol
  }) {
    const provider = await this.getProvider();
    return await provider.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address,
          decimals,
          image,
          symbol
        }
      }
    });
  }

  async fetchBalance(address) {
    const provider = await this.getProvider();
    const balance = await provider.request({
      method: 'eth_getBalance',
      params: [address, 'latest']
    });
    return toBN(balance === null || balance === void 0 ? void 0 : balance.toString());
  }

  attachProxy(contract) {
    // wallet connect custom their own error message, so will need to manual check if contract call was rejected by user or not
    const handler = {
      get(target, prop, receiver) {
        const contractMethod = target[prop];

        if (contractMethod instanceof Function) {
          return (...contractArgs) => {
            const context = this === receiver ? target : this;
            const result = contractMethod.apply(context, contractArgs);
            const child = new Proxy(result, {
              get(childTarget, childProp, childReceiver) {
                const childValue = childTarget[childProp];

                if ((childProp === 'call' || childProp === 'send') && childValue instanceof Function) {
                  return async (...childArgs) => {
                    try {
                      const result = await childValue.apply(this === childReceiver ? childTarget : this, childArgs);
                      return result;
                    } catch (error) {
                      if (isUserRejectedRequestError(error)) throw new UserRejectedRequestError(error);else {
                        throw error;
                      }
                    }
                  };
                }

                return childValue;
              }

            });
            return child;
          };
        }

        return contractMethod;
      }

    };
    const proxy = new Proxy(contract.methods, handler);
    contract.methods = proxy;
    return contract;
  }

}

const isUserRejectedRequestError = error => {
  const message = typeof error === 'string' ? error : error === null || error === void 0 ? void 0 : error.message;
  return /user rejected/i.test(message);
};